import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { Toaster } from 'sonner'
import { bootAppEmbedding } from './lib/app-embedding'
import { bootAppHeight } from './lib/app-height'
import { bootAppearance } from './lib/appearance'
import { setupPwaInstallCapture } from './lib/pwa-install'
import { registerAppServiceWorker } from './lib/sw-register'
import { bootTheme } from './lib/theme'
import { useIsMobile } from './lib/use-media-query'
import { useToastBottomInset } from './lib/use-toast-bottom-inset'
import { router } from './router'
import './index.css'

bootTheme()
bootAppearance()
bootAppEmbedding()
// PWA-Kaltstart: --app-height nachführen, bevor die Shell erstmals malt —
// sonst hängt die Bottom-Bar bei stale iOS-Standalone-Viewport „mittig".
bootAppHeight()
// beforeinstallprompt kommt genau einmal und oft VOR dem React-Mount —
// hier im Modul-Scope abfangen, sonst verpasst das Install-Sheet es.
setupPwaInstallCapture()
// Service worker for the offline app shell (#785) — independent of the
// push opt-in, production builds only (Vite dev has no stable assets).
if (import.meta.env.PROD) {
  registerAppServiceWorker().catch(() => undefined)
}

const queryClient = new QueryClient({
  defaultOptions: {
    // refetchOnWindowFocus: Zweitfenster/Tab-Wechsel holt frische Daten —
    // vorher war Invalidierung der EINZIGE Update-Pfad und ein zweites
    // Fenster blieb für immer stale (Audit „Widgets nicht geupdatet").
    // staleTime 30s verhindert dabei Refetch-Stürme beim Tab-Jonglieren.
    queries: { retry: false, refetchOnWindowFocus: true, staleTime: 30_000 },
  },
})

/** Desktop bleibt bottom-right mit 80px Abstand. Mobile Toasts stehen über
 * der 56px-Tab-Leiste bzw. Tastatur, ohne die Kopfzeile zu verdecken. */
function AppToaster() {
  const isMobile = useIsMobile()
  const bottomInset = useToastBottomInset(isMobile)
  const mobileOffset = {
    bottom: `calc(${80 + bottomInset}px + var(--app-safe-bottom))`,
  }
  return (
    <Toaster
      position={isMobile ? 'bottom-center' : 'bottom-right'}
      richColors
      offset={isMobile ? mobileOffset : 80}
      mobileOffset={mobileOffset}
      toastOptions={{
        style: {
          background: 'rgb(var(--c-bg-elevated))',
          border: '1px solid rgb(var(--c-border) / 0.10)',
          color: 'rgb(var(--c-text-primary))',
          maxWidth: '320px',
        },
      }}
    />
  )
}

const rootElement = document.getElementById('root')
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <AppToaster />
      </QueryClientProvider>
    </StrictMode>,
  )
}
